import { useMemo, useState } from "react";

export const useSelectionState = <T extends { id: string | number }>(
  allItems: T[],
) => {
  const [selectedItemIds, setSelectedItemIds] = useState(new Set<T["id"]>());
  const [lastSelectedId, setLastSelectedId] = useState<T["id"]>();

  const actions = useMemo(
    () => ({
      selectItems: (items: T[], fillRange = false) => {
        const itemsCopy = [...items];
        if (items.length === 1) {
          if (
            lastSelectedId !== undefined &&
            selectedItemIds.has(lastSelectedId) &&
            fillRange
          ) {
            const lastSelectedIdx = allItems.findIndex(
              (item) => item.id === lastSelectedId,
            );

            const itemIdx = allItems.findIndex(
              (item) => item.id === items[0].id,
            );
            if (
              itemIdx > -1 &&
              lastSelectedIdx > -1 &&
              lastSelectedIdx !== itemIdx
            ) {
              const rangeSlice =
                itemIdx > lastSelectedIdx
                  ? allItems.slice(lastSelectedIdx, itemIdx)
                  : allItems.slice(itemIdx, lastSelectedIdx);

              itemsCopy.push(...rangeSlice);
            }
          }
          setLastSelectedId(items[0].id);
        }
        setSelectedItemIds((previousItemIds) => {
          const newSelectedRows = new Set(previousItemIds);
          itemsCopy.forEach((item) => newSelectedRows.add(item.id));
          return newSelectedRows;
        });
      },
      deselectItems: (items: Pick<T, "id">[]) => {
        setLastSelectedId(undefined);
        setSelectedItemIds((previousItems) => {
          const newSelectedRows = new Set(previousItems);
          items.forEach((item) => newSelectedRows.delete(item.id));
          return newSelectedRows;
        });
      },
      deselectAll: () => {
        setSelectedItemIds(new Set());
        setLastSelectedId(undefined);
      },
      selectAll: () => {
        setLastSelectedId(undefined);
        setSelectedItemIds(new Set(allItems.map((item) => item.id)));
      },
    }),
    [allItems, lastSelectedId, selectedItemIds],
  );

  // Convert selected items into a lookup map. This isn't strictly necessary but
  // supports legacy code without the need for larger changes.
  const readOnlyItems = useMemo(() => {
    const readOnlyItems = new Map<T["id"], T>();
    const allItemsById = new Map(allItems.map((item) => [item.id, item]));
    selectedItemIds.forEach((itemId) => {
      const item = allItemsById.get(itemId);
      if (item !== undefined) {
        readOnlyItems.set(item.id, item);
      }
    });
    return readOnlyItems as ReadonlyMap<T["id"], T>;
  }, [allItems, selectedItemIds]);

  return [readOnlyItems, actions] as const;
};
